import sortBy from 'lodash/sortBy'
import React from 'react'
import {
  AbsoluteFill,
  Easing,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion'
import {
  ResponsiveSceneProvider,
  useResponsiveSceneProps,
} from '../components/ResponsiveSceneContext'
import StarSvg from '../components/StarSvg'
import { TextFit } from '../components/TextFit'
import { SceneProps } from '../types'

export function KeywordsScene({
  audiogram,
  orientation,
  scene,
}: SceneProps) {
  const keywords = sortBy(audiogram.wordMap, (w) => w.count)
    .reverse()
    .map((w) => w.word)

  return (
    <ResponsiveSceneProvider
      audiogram={audiogram}
      scene={scene}
      orientation={orientation}
    >
      <Background />

      <EdgeStripe />
      <StarLine />

      <TitleText />

      <MainKeyword text={keywords[0]} />

      <Keywords words={keywords.slice(1)} />
    </ResponsiveSceneProvider>
  )
}

const Background = () => {
  const { audiogram } = useResponsiveSceneProps()

  return (
    <AbsoluteFill style={{ backgroundColor: audiogram.theme.colors.primary }} />
  )
}

const EdgeStripe = () => {
  const { audiogram, u } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const time = frame / fps
  const scale = interpolate(time - 0.1, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  return (
    <div
      className="absolute"
      style={{
        backgroundColor: audiogram.theme.colors.accent,
        top: 0,
        height: u(6),
        left: 0,
        width: u(2),
        transform: `translate(-50%,-50%) scaleY(${scale}) translate(50%,50%)`,
        opacity: scale,
      }}
    />
  )
}

const StarLine = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const rate = 0.25 * 360 * time

  const enterDriver = interpolate(time, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  const x = enterDriver * (w() - u(2))

  return (
    <>
      <div
        className="absolute -translate-y-1/2"
        style={{
          backgroundColor: audiogram.theme.colors.secondary,
          top: u(6),
          width: x,
          height: u(0.125),
        }}
      />
      <StarSvg
        className="absolute"
        style={{
          color: audiogram.theme.colors.secondary,
          top: u(6),
          width: u(3),
          height: u(3),
          transform: `translate(-50%,-50%) translateX(${x}px) rotate(${-rate}deg)`,
        }}
      />
    </>
  )
}

const TitleText = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()

  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const enterDriver = interpolate(time, [0, 0.5], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const opacity = enterDriver
  const x = interpolate(enterDriver, [0, 1], [-u(2), 0], {
    easing: Easing.out(Easing.exp),
  })

  return (
    <div
      className="absolute flex flex-row items-center overflow-hidden text-6xl"
      style={{
        color: audiogram.theme.colors.secondary,
        right: u(2),
        height: u(5),
        left: u(3),
        top: u(0.5),
        fontFamily: audiogram.theme.fonts.primary,
        lineHeight: 1.2,
        opacity,
        transform: `translateX(${x}px)`,
      }}
    >
      Keywords
    </div>
  )
}

const MainKeyword = ({ text }: { text: string }) => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()

  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const enterDriver = interpolate(time, [0, 0.5], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const opacity = enterDriver
  const x = interpolate(enterDriver, [0, 1], [u(0.5), 0], {
    easing: Easing.out(Easing.exp),
  })

  const sizing = {
    landscape: {
      height: u(10),
      left: u(3),
      bottom: u(3),
      right: u(3),
      transform: `translateX(${x}px)`,
    },
    portrait: {
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      height: u(12),
      width: h() - u(22),
      left: u(14),
      top: u(12),
      transform: `translateY(${
        2 * x
      }px) translate(-100%, -100%) translate(50%, 50%) rotate(-90deg) translate(-50%, -50%)`,
    },
    square: {
      height: u(8),
      left: u(2),
      bottom: u(4),
      right: u(2),
      transform: `translateX(${x}px)`,
    },
  }[orientation]

  return (
    <TextFit
      maxFontSize={u(10)}
      className="absolute flex flex-row"
      style={{
        // writingMode: 'vertical-lr',
        ...sizing,
        color: audiogram.theme.colors.secondary,
        fontFamily: audiogram.theme.fonts.primary,
        lineHeight: 1,
        opacity,
      }}
      text={text}
    />
  )
}

const Keywords = ({ words }: { words: string[] }) => {
  const { u, w, h, orientation } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const time = frame / fps

  const sizing = {
    landscape: {
      bottom: u(14),
      left: u(3),
      right: u(50),
      transform:
        'translate(0%, 100%) translate(-50%, -50%) rotate(-90deg) translate(50%, 50%)',
    },
    portrait: {
      top: u(12),
      left: u(16),
      bottom: u(3),
    },
    square: {
      bottom: u(13),
      left: u(1),
      right: h() - u(12),
      transform:
        'translate(0%, 100%) translate(-50%, -50%) rotate(-90deg) translate(50%, 50%)',
    },
  }[orientation]

  const x = (t: number) =>
    interpolate(t, [0, 0.5], [u(3), 0], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.out(Easing.exp),
    })

  const opacity = (t: number) =>
    interpolate(t, [0, 0.25], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })

  const item = (t: number) => ({
    opacity: opacity(t),
    transform: `translateX(${x(t)}px)`,
  })

  return (
    <div className="absolute flex flex-col overflow-hidden" style={sizing}>
      {words.map((word, index) => (
        <React.Fragment key={`word:${index}:${word}`}>
          <div className="my-4" style={item(time - 0.1 * index)}>
            <SubKeyword size={u(5)} text={word} />
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

const SubKeyword = ({ size, text }: { size: number; text: string }) => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()

  return (
    <div
      className="leading-none"
      style={{
        color: audiogram.theme.colors.secondary,
        fontFamily: audiogram.theme.fonts.primary,
        fontSize: 0.5 * size,
      }}
    >
      {text}
    </div>
  )
}
